import React from 'react';
import classNames from 'classnames';

import { RCCloseButton, RCThemeOverride } from '../';
import { RCPopoverBase } from '../internalComponents/RCPopoverBase';
import type { PopoverAnchoring } from '../internalComponents/RCPopoverBase';

type FloatingUIPositions = 'bottom' | 'bottom-start' | 'bottom-end';

type RCGuidePopoverProps = {
  /** Open state of the component (controlled by the parent). */
  isOpen: boolean,

  /** Fired when the user clicks the dismissal button or presses escape (on Desktop Web) or taps outside the component (on Mobile Web). */
  onDismiss: Function,

  /** The primary text. Keep this below about 60 characters. On Mobile Web, this will the only text that displays. */
  title: string,

  /** *Desktop Only* - A string or block of HTML that renders in the popover between the header and dismiss button.*/
  content: JSX.Element | string,

  /** The popover automatically positions itself,
   * but if you need to ensure it is always flush left or right (e.g. to avoid covering up key UI, use this. */
  position?: FloatingUIPositions,

  /**  If you set this to true, the popover will stay in-viewport even if the user scrolls away from its anchor. */
  sticky?: boolean,

  /** `Narrow` sets a reduced max width for the element; use this in areas where you need to minimize content overlap. */
  width?: 'default' | 'narrow',

  /** Prevents the popover and its anchor from stealing focus when it opens. Should be
   * used when RCGuidePopover opens on page load or is not otherwise triggered by a user action. */
  preventAutoFocus?: boolean;
} & PopoverAnchoring;

export function RCGuidePopover(props: RCGuidePopoverProps) {
  const {
    isOpen,
    onDismiss,
    title,
    position = 'bottom-end',
    sticky = false,
    content,
    width = 'default',
    preventAutoFocus = false,
  } = props;
  const classes = classNames(
    'rc-guide-popover',
    { 'rc-guide-popover--narrow': width === 'narrow' },
  );

  // The trigger doesn't open a guide popover (the consumer controls it), so it gets no click
  // handling or `aria-expanded`.
  return (
    <RCPopoverBase.Root
      isOpen={isOpen}
      onOpenChange={(state) => {
        if (state === true) return;
        onDismiss();
      }}
      sticky={sticky}
      position={position}
      dismissOnOutsidePress={false}
      openOnClick={false}
      triggerControlsPanel={false}
    >
      {props.anchor
        ? <RCPopoverBase.Trigger>{props.anchor}</RCPopoverBase.Trigger>
        : <RCPopoverBase.Anchor anchorRef={props.anchorRef} />
      }
      <RCPopoverBase.Panel
        className="rc-guide-popover__panel"
        trapFocus={false}
        initialFocus={preventAutoFocus ? -1 : 0}
        closeOnFocusOut={false}
      >
        <div className={classes}>
          <RCPopoverBase.Heading className="rc-guide-popover__title">
            {title}
          </RCPopoverBase.Heading>
          <RCPopoverBase.Description className="rc-guide-popover__content">
            {content}
          </RCPopoverBase.Description>
          <RCThemeOverride theme="dark">
            <div className="rc-guide-popover__close-button">
              <RCCloseButton
                variant="transparent"
                size="small"
                onClick={() => onDismiss()}
                useAltIcon
              />
            </div>
          </RCThemeOverride>
        </div>
        <RCPopoverBase.Arrow />
      </RCPopoverBase.Panel>
    </RCPopoverBase.Root>
  );
}
